import { z } from 'zod';

import type { PropertyCategory, PropertySubtype } from '../common/property-taxonomy.schema';
import type { TransactionType } from '../common/transaction-type.schema';

export const COMMISSION_BASES = ['PERCENT_OF_PRICE', 'FIXED_AMOUNT'] as const;
export const CommissionBasisSchema = z.enum(COMMISSION_BASES);
export type CommissionBasis = z.infer<typeof CommissionBasisSchema>;

/** Every statement adds this rate (parent Resolved Decision 13); the copy names it literally. */
export const COMMISSION_VAT_RATE_PERCENT = 19;
export const COMMISSION_RATE_MAX_PERCENT = 100;
/** numeric(12, 2). */
export const COMMISSION_AMOUNT_MAX = 9_999_999_999.99;

export const CommissionStatementSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('RATE'), grossRate: z.number() }),
  z.object({ kind: z.literal('AMOUNT'), grossAmount: z.number() }),
  z.object({ kind: z.literal('BUYER_FREE') }),
  z.object({ kind: z.literal('TENANT_FREE') }),
]);
export type CommissionStatement = z.infer<typeof CommissionStatementSchema>;

/** The stored facts a statement is derived from. `commission` is the legacy free text. */
export type SaleCommissionFacts = {
  readonly hasCommission: boolean;
  readonly commissionBasis: CommissionBasis | null;
  readonly commissionRateNet: number | null;
  readonly commissionAmountNet: number | null;
  readonly commission: string | null;
};

/** Net → gross at 19 %, in integer cents so 3,50 → 4,17 and never 4,16 (float 3.5 × 1.19 = 4.1649…). */
export function grossCommissionOf(net: number): number {
  const netCents = Math.round(net * 100);

  return Math.round((netCents * (100 + COMMISSION_VAT_RATE_PERCENT)) / 100) / 100;
}

/** The statement a sale listing carries, or `null` while it is incomplete. */
export function saleCommissionStatementOf(facts: SaleCommissionFacts): CommissionStatement | null {
  if (!facts.hasCommission) return { kind: 'BUYER_FREE' };

  if (facts.commissionBasis === 'PERCENT_OF_PRICE' && facts.commissionRateNet !== null) {
    return { kind: 'RATE', grossRate: grossCommissionOf(facts.commissionRateNet) };
  }

  if (facts.commissionBasis === 'FIXED_AMOUNT' && facts.commissionAmountNet !== null) {
    return { kind: 'AMOUNT', grossAmount: grossCommissionOf(facts.commissionAmountNet) };
  }

  return null;
}

/** The buyer side of a Deal, derived from its Objekt (IMM-1380). Net figures; the statement is gross. */
export type BuyerCommissionTerms = {
  readonly buyerCommission: CommissionStatement | null;
  /** Net rate; 0 when BUYER_FREE; null for AMOUNT or fehlt. */
  readonly buyerCommissionPercent: number | null;
  /** Net amount; only for AMOUNT. */
  readonly buyerCommissionAmountNet: number | null;
};

export const NO_BUYER_COMMISSION: BuyerCommissionTerms = {
  buyerCommission: null,
  buyerCommissionPercent: null,
  buyerCommissionAmountNet: null,
};

export function buyerCommissionTermsOf(facts: SaleCommissionFacts): BuyerCommissionTerms {
  const statement = saleCommissionStatementOf(facts);

  if (statement === null) return NO_BUYER_COMMISSION;

  if (statement.kind === 'RATE') {
    return {
      buyerCommission: statement,
      buyerCommissionPercent: facts.commissionRateNet,
      buyerCommissionAmountNet: null,
    };
  }

  if (statement.kind === 'AMOUNT') {
    return {
      buyerCommission: statement,
      buyerCommissionPercent: null,
      buyerCommissionAmountNet: facts.commissionAmountNet,
    };
  }

  return { buyerCommission: statement, buyerCommissionPercent: 0, buyerCommissionAmountNet: null };
}

export const COMMISSION_VALUE_ISSUES = ['RATE_OUT_OF_RANGE', 'AMOUNT_NOT_POSITIVE'] as const;
export type CommissionValueIssue = (typeof COMMISSION_VALUE_ISSUES)[number];

/** The dialog's check and the domain's refusal are this one function. `Number.isFinite` first: NaN passes every range comparison (BL-138). */
export function commissionValueIssueOf(input: {
  readonly commissionRateNet: number | null;
  readonly commissionAmountNet: number | null;
}): CommissionValueIssue | null {
  const rate = input.commissionRateNet;
  const amount = input.commissionAmountNet;

  if (rate !== null && !(Number.isFinite(rate) && rate >= 0 && rate <= COMMISSION_RATE_MAX_PERCENT)) {
    return 'RATE_OUT_OF_RANGE';
  }

  if (amount !== null && !(Number.isFinite(amount) && amount > 0)) return 'AMOUNT_NOT_POSITIVE';

  return null;
}

/** A sale Objekt with no price row reads as the column default, „Provision aktiv“ on (has_commission DEFAULT true).
 *  Takes the domain `PricingSale` and the wire `PropertyPricingSaleView` alike, so the backend rules and the web
 *  app's share refusal (IMM-1168.3) read the same facts (BL-082). */
export function saleCommissionFactsOf(
  pricingSale: (SaleCommissionFacts & { readonly commissionNote: string | null }) | null,
): SaleCommissionFacts {
  return {
    hasCommission: pricingSale?.hasCommission ?? true,
    commissionBasis: pricingSale?.commissionBasis ?? null,
    commissionRateNet: pricingSale?.commissionRateNet ?? null,
    commissionAmountNet: pricingSale?.commissionAmountNet ?? null,
    commission: pricingSale?.commission ?? null,
  };
}

export const BUYER_COMMISSION_GAPS = ['MISSING', 'REENTRY_REQUIRED'] as const;
export type BuyerCommissionGap = (typeof BUYER_COMMISSION_GAPS)[number];

/** Why a sale commission cannot be published yet, or `null` when it can (parent DBR „Complete means“). */
export function buyerCommissionGapOf(facts: SaleCommissionFacts): BuyerCommissionGap | null {
  if (saleCommissionStatementOf(facts) !== null) return null;

  return facts.commission === null || facts.commission.trim() === '' ? 'MISSING' : 'REENTRY_REQUIRED';
}

/** § 2 Abs. 1a WoVermittG: a Wohnung or Haus for rent is advertised commission-free for the tenant (parent DBR). */
export function isResidentialRental(category: PropertyCategory, marketingType: TransactionType): boolean {
  return marketingType === 'RENTAL' && (category === 'APARTMENT' || category === 'HOUSE');
}

/** §§ 656c/d BGB reach a Wohnung and an Einfamilienhaus; the PRD reads that as every Haus but a Mehrfamilienhaus (parent DBR). */
export function halbteilungAppliesTo(category: PropertyCategory, subtype: PropertySubtype | null): boolean {
  return category === 'APARTMENT' || (category === 'HOUSE' && subtype !== 'MULTI_FAMILY_HOUSE');
}
